import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import { isNull, isUndefined } from 'lodash'
import groupBy from 'lodash/groupBy'
import sortBy from 'lodash/sortBy'
import uniq from 'lodash/uniq'
import moment from 'moment'

import { observer } from 'mobx-react'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import CustomFields from 'modules/custom_resources/store/collections/customFields/fields'
import CustomValues from 'modules/custom_resources/store/collections/customFields/values'
import type CustomField from 'modules/custom_resources/store/models/customFields/Field'
import type { ICustomField } from 'modules/custom_resources/store/models/customFields/Field'
import QuestionsWithCategory from 'modules/surveys/components/WriteResponse/QuestionsWithCategory'
import QuestionsWithoutCategory from 'modules/surveys/components/WriteResponse/QuestionsWithoutCategory'
import coerceValueFromType from 'modules/surveys/lib/coerceValueFromType'
import type { IQuestionCategory } from 'modules/surveys/store/models/QuestionCategory'
import type QuestionGroup from 'modules/surveys/store/models/QuestionGroup'

type QuestionsWithoutCategoryProps = React.ComponentProps<typeof QuestionsWithoutCategory>
type QuestionsWithCategoryProps = React.ComponentProps<typeof QuestionsWithCategory>

// Define the question payload type based on QuestionGroup interface
type QuestionPayload = {
  custom_field: ICustomField
  position: number
  surveys_question_category_id: string
}

// Define the question with category type that includes the surveys_question_category object
type QuestionWithCategoryObject = QuestionPayload & {
  surveys_question_category: IQuestionCategory
}

// Define the type for the mapped result that can be either QuestionPayload or ICustomField with position
type MappedQuestionPayload = QuestionPayload | (ICustomField & { position: number })

// Define the response data type
type ResponseData = {
  surveys_question_group_id: string
  answers: Record<string, unknown>
  is_draft: boolean
}

function missingMandatory(forms: Form[]): boolean {
  return Boolean(
    forms
      .filter((form) => Boolean(form.get('mandatory').value))
      .find((form) => {
        const value = form.get('value').value

        return isNull(value) || isUndefined(value) || value === ''
      })
  )
}

function isFormDirty(forms: Form[]): boolean {
  return forms.some((form) => form.isDirty)
}

function clearForms(forms: Form[]) {
  forms.map((form) => form.cleanAll())
}

interface Props {
  questionGroup: QuestionGroup
  onSubmit?: () => void
  onDraftSaved: () => Promise<void>
  canReview: boolean
}

function ResponseQuestions({ questionGroup, onSubmit, onDraftSaved, canReview }: Props) {
  const questionsWithCategory = questionGroup.withCategory
  const questionsWithoutCategory = questionGroup.withoutCategory

  const filterByCategory = React.useRef(
    groupBy(questionsWithCategory, (q) => q.surveys_question_category_id)
  ).current

  const uniqueCategories = React.useRef(
    uniq(
      questionsWithCategory
        .map((q) => q.surveys_question_category_id)
        .sort((a, b) => Number(a) - Number(b))
    )
  ).current

  const createForm = (field: CustomField, value: string | null | boolean | number) => {
    const values = {
      field_id: field.attributes.id,
      field_type: field.attributes.field_type,
      mandatory: field.attributes.required,
      name: field.attributes.label,
      min_value: field.attributes.min_value,
      max_value: field.attributes.max_value,
      value,
    }

    return new Form(values, {
      field_id: 'string',
      field_type: 'string',
      mandatory: 'boolean',
      name: 'string',
      min_value: 'number',
      max_value: 'number',
      value: 'string',
    })
  }

  const onFormSubmittedOrDrafted = () => {
    clearForms(formsWithoutCategory)
    clearForms(formsWithCategory)
  }

  const isENPS = questionGroup.form?.isEnps

  // Questions without category
  const customFieldsWithoutCategory = React.useRef(
    new CustomFields(
      questionsWithoutCategory.map((payload: QuestionPayload) => {
        if (!payload.custom_field) return payload

        return { ...payload.custom_field, position: payload.position }
      }),
      {
        model_ref: 'surveys-question_group',
        instance_id: questionGroup.attributes.id,
      }
    )
  ).current

  const customValuesWithoutCategory = React.useRef(
    new CustomValues(questionGroup.attributes.answers, {
      model_ref: 'QuestionGroup',
      instance_id: questionGroup.attributes.id,
    })
  ).current

  const optionsWithoutCategory = React.useMemo(() => {
    return customFieldsWithoutCategory.models.reduce<QuestionsWithoutCategoryProps['options']>(
      (memo, cf) => {
        memo[cf.attributes.id] = cf.choiceOptions.filter((opt) => opt.isActive)

        return memo
      },
      {}
    )
  }, [customFieldsWithoutCategory])

  const formsWithoutCategory = React.useRef(
    sortBy(customFieldsWithoutCategory.models, (cf) => cf.attributes.position).map((field) => {
      const answer = customValuesWithoutCategory.find({
        field_id: field.attributes.id,
      })

      const value = answer ? coerceValueFromType(field, answer.attributes.value) : ''

      return createForm(field, value)
    })
  ).current

  // Questions With category
  const customFieldsWithCategory = React.useRef(
    new CustomFields(
      questionsWithCategory.map((payload: QuestionPayload) => {
        if (!payload.custom_field) return payload

        return { ...payload.custom_field, position: payload.position }
      }),
      {
        model_ref: 'surveys-question_group',
        instance_id: questionGroup.attributes.id,
      }
    )
  ).current

  const customValuesWithCategory = React.useRef(
    new CustomValues(questionGroup.attributes.answers, {
      model_ref: 'QuestionGroup',
      instance_id: questionGroup.attributes.id,
    })
  ).current

  const optionsWithCategory = React.useMemo(() => {
    return customFieldsWithCategory.models.reduce<
      QuestionsWithCategoryProps['optionsWithCategory']
    >((memo, cf) => {
      memo[cf.get('id')] = cf.choiceOptions.filter((opt) => opt.isActive)

      return memo
    }, {})
  }, [customFieldsWithCategory])

  const formsWithCategory = React.useRef<Form[]>(
    sortBy(customFieldsWithCategory.models, (cf) => cf.get('position')).map((field) => {
      const answer = customValuesWithCategory.find({
        field_id: field.get('id'),
      })

      const value = answer ? coerceValueFromType(field, answer.attributes.value) : ''

      return createForm(field, value)
    })
  ).current

  const computeData = ({ isDraft = false } = {}) => {
    const data: ResponseData = {
      surveys_question_group_id: questionGroup.attributes.id,
      answers: {},
      is_draft: isDraft,
    }

    formsWithoutCategory.forEach((form) => {
      const fieldId = form.get('field_id').value
      const value = form.get('value').value

      if (value !== undefined) data.answers[fieldId] = value
    })
    formsWithCategory.forEach((form: Form) => {
      const fieldId = form.get('field_id').value
      const value = form.get('value').value

      if (value !== undefined) data.answers[fieldId] = value
    })

    return data
  }

  const eventName = isENPS
    ? trackingEvents.enps.submitEnpsAnswer
    : trackingEvents.surveys.submitAnswer

  const {
    success: successSubmit,
    value: valueSubmit,
    running: runningSubmit,
    performAction: onSubmitAnswer,
  } = useAsyncAction(async () => {
    const data = computeData()
    await questionGroup.rpc('create_values', data)

    const answeredAtData = {
      answered_at: dateToString(moment(), 'backend'),
    }
    questionGroup.set(answeredAtData)
    onFormSubmittedOrDrafted()
    onSubmit && onSubmit()
    track(eventName, {
      is_successful: successSubmit,
      error_code: !successSubmit && JSON.stringify(valueSubmit),
    })
  })

  const {
    success: successDraft,
    value: valueDraft,
    running: runningDraft,
    performAction: onSaveDraft,
  } = useAsyncAction(
    async () => {
      const data = computeData({
        isDraft: true,
      })
      await questionGroup.rpc('create_values', data)

      const draftedAt = {
        drafted_at: dateToString(moment(), 'backend'),
      }
      questionGroup.set(draftedAt)
      onFormSubmittedOrDrafted()
      onDraftSaved && onDraftSaved()
    },
    {
      successMessage: i18n.t('common.draft_saved'),
    }
  )

  return (
    <>
      {!!formsWithoutCategory.length && (
        <Box gap="s48" padding="s24">
          {formsWithoutCategory.map((form: Form, index: number) => {
            const field = customFieldsWithoutCategory.get(form.get('field_id').value)

            if (!field) {
              return null
            }

            return (
              <QuestionsWithoutCategory
                canReview={canReview}
                key={index}
                form={form}
                isResponseValues={questionGroup.isClosed}
                options={optionsWithoutCategory}
                field={field}
              />
            )
          })}
        </Box>
      )}
      {uniqueCategories &&
        uniqueCategories.map((categoryId) => {
          const firstFilter = filterByCategory[categoryId]?.[0] as
            | QuestionWithCategoryObject
            | undefined

          if (!firstFilter) {
            return null
          }

          return (
            <QuestionsWithCategory
              canReview={canReview}
              key={firstFilter.surveys_question_category.name}
              categoryName={firstFilter.surveys_question_category.name}
              questionCategories={filterByCategory[categoryId] ?? []}
              formsWithCategory={formsWithCategory}
              optionsWithCategory={optionsWithCategory}
              isResponseValues={questionGroup.isClosed}
            />
          )
        })}
      {canReview && (
        <Box justifyContent="flexEnd" flexDirection="row" flexGrow padding="s24" gap="s16">
          <Button
            type="secondary"
            onClick={onSaveDraft}
            disabled={
              runningDraft ||
              runningSubmit ||
              !(isFormDirty(formsWithoutCategory) || isFormDirty(formsWithCategory))
            }
          >
            {questionGroup.isDraft
              ? i18n.t('surveys.form.response.update_draft')
              : i18n.t('surveys.form.response.save_draft')}
          </Button>
          <Button
            onClick={onSubmitAnswer}
            disabled={
              runningDraft ||
              runningSubmit ||
              missingMandatory(formsWithoutCategory) ||
              missingMandatory(formsWithCategory)
            }
          >
            {i18n.t('surveys.form.response.submit')}
          </Button>
        </Box>
      )}
    </>
  )
}

export default observer(ResponseQuestions)
